import React, { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import {
  COOKIEYES_CATEGORY,
  readStoredCookieYesConsent,
  subscribeToCookieYesConsent,
} from 'utils/cookieYesConsent';

interface GoogleAnalyticsContextValue {
  isEnabled: boolean;
  trackEvent: (name: string, params?: Record<string, unknown>) => void;
}

const GoogleAnalyticsContext = createContext<GoogleAnalyticsContextValue>({
  isEnabled: false,
  trackEvent: () => undefined,
});

const isGaEnabled = (): boolean =>
  window.runConfig?.nodeEnv === 'prod' && !!window.runConfig?.gaMeasurementId;

const buildConsentUpdate = (accepted: string[]) => {
  const analyticsGranted = accepted.includes(COOKIEYES_CATEGORY.analytics);
  const adsGranted = accepted.includes(COOKIEYES_CATEGORY.advertisement);

  return {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: adsGranted ? 'granted' : 'denied',
    ad_user_data: adsGranted ? 'granted' : 'denied',
    ad_personalization: adsGranted ? 'granted' : 'denied',
  } as const;
};

export function GoogleAnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const enabled = isGaEnabled();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const measurementId = window.runConfig.gaMeasurementId as string;

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag(...args: unknown[]) {
        window.dataLayer?.push(args);
      };

    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500,
    });

    const storedAccepted = readStoredCookieYesConsent();
    if (storedAccepted.length) {
      window.gtag('consent', 'update', buildConsentUpdate(storedAccepted));
    }

    ReactGA.initialize(measurementId, {
      gtagOptions: { send_page_view: false },
    });

    return subscribeToCookieYesConsent((accepted) => {
      window.gtag?.('consent', 'update', buildConsentUpdate(accepted));
    });
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !ReactGA.isInitialized) {
      return;
    }

    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title,
    });
  }, [enabled, location.pathname, location.search]);

  const trackEvent = (name: string, params?: Record<string, unknown>) => {
    if (!enabled || !ReactGA.isInitialized) {
      return;
    }
    ReactGA.event(name, params);
  };

  return (
    <GoogleAnalyticsContext.Provider value={{ isEnabled: enabled, trackEvent }}>
      {children}
    </GoogleAnalyticsContext.Provider>
  );
}

export const useGoogleAnalytics = () => useContext(GoogleAnalyticsContext);
